import { Link } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import SecaoManifesto from '../components/SecaoManifesto'
import VideoExplicativo from '../components/VideoExplicativo'

export default function Landing() {
  const { user, perfil } = useAuth()
  const homeRota = perfil?.role === 'medico' ? '/medico' : '/app'

  return (
    <div
      className="min-h-screen text-white relative overflow-hidden"
      style={{
        background: `
          radial-gradient(ellipse at 20% 30%, rgba(37,150,190,0.45), transparent 55%),
          radial-gradient(ellipse at 80% 10%, rgba(168,230,240,0.18), transparent 50%),
          radial-gradient(ellipse at 70% 90%, rgba(37,150,190,0.25), transparent 55%),
          linear-gradient(135deg, #061826 0%, #0a2a40 35%, #0d4a60 65%, #061a2e 100%)
        `,
      }}
    >
      <Decorations />

      <div className="relative z-10">
        {/* HEADER */}
        <header className="px-6 py-5 max-w-7xl mx-auto flex items-center justify-between">
          <Logo />
          <nav className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <Link
                to={homeRota}
                className="rounded-full px-5 py-2 text-sm font-semibold bg-accent-turquesa text-deep-900 hover:scale-105 transition shadow-lg shadow-accent-turquesa/20"
              >
                Abrir app →
              </Link>
            ) : (
              <>
                <Link
                  to="/medicos"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-white/85 hover:text-white transition hidden md:inline-block"
                >
                  🩺 Médicos
                </Link>
                <Link
                  to="/psicologos"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-white/85 hover:text-white transition hidden md:inline-block"
                >
                  🧠 Psicólogos
                </Link>
                <Link
                  to="/faq"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-white/85 hover:text-white transition hidden lg:inline-block"
                >
                  Dúvidas
                </Link>
                <Link
                  to="/login"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-white/85 hover:text-white transition"
                >
                  Entrar
                </Link>
                <Link
                  to="/cadastro?role=paciente"
                  className="rounded-full px-5 py-2 text-sm font-semibold bg-accent-turquesa text-deep-900 hover:scale-105 transition shadow-lg shadow-accent-turquesa/20"
                >
                  Criar conta
                </Link>
              </>
            )}
          </nav>
        </header>

        {/* HERO */}
        <section className="max-w-7xl mx-auto px-6 pt-12 md:pt-24 pb-16 md:pb-32 animate-fade-up">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-7">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-[11px] font-semibold tracking-[0.18em] uppercase text-white/85 mb-7">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-turquesa opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-turquesa" />
                </span>
                Pra pacientes · Grátis pra sempre
              </span>

              <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.98] mb-7 text-balance">
                Acompanhe<br />
                sua <em className="text-gradient-shimmer not-italic">saúde</em><br />
                no dia a dia.
              </h1>

              <p className="text-lg md:text-xl text-white/85 leading-[1.7] font-light max-w-2xl mb-10 text-pretty">
                Registre como você está se sentindo em <strong className="text-white">2 minutos por dia</strong> —
                humor, sintomas, sono. Quando for à consulta, seu profissional já chega com seu
                histórico organizado, sem você precisar lembrar de tudo.
              </p>

              <div className="flex flex-wrap gap-3 mb-6">
                <Link
                  to="/cadastro?role=paciente"
                  className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold bg-accent-turquesa text-deep-900 hover:scale-105 active:scale-100 transition shadow-2xl shadow-accent-turquesa/25"
                >
                  Criar conta grátis
                  <span className="transition group-hover:translate-x-1">→</span>
                </Link>
                <Link
                  to="/login"
                  className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold bg-white/10 backdrop-blur-sm border border-white/25 hover:bg-white/20 hover:border-white/40 transition"
                >
                  Já tenho conta
                  <span className="transition group-hover:translate-x-1">→</span>
                </Link>
              </div>
              <div className="mb-10 flex flex-wrap gap-x-4 gap-y-2 text-sm">
                <Link to="/medicos" className="text-white/70 hover:text-white underline underline-offset-4 decoration-white/30 hover:decoration-white transition">
                  🩺 É médico? Veja como funciona
                </Link>
                <Link to="/psicologos" className="text-white/70 hover:text-white underline underline-offset-4 decoration-white/30 hover:decoration-white transition">
                  🧠 É psicólogo? Veja como funciona
                </Link>
              </div>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/70">
                <span className="flex items-center gap-1.5"><Tick /> PWA · iOS · Android · Web</span>
                <span className="flex items-center gap-1.5"><Tick /> LGPD compliant</span>
                <span className="flex items-center gap-1.5"><Tick /> HL7/FHIR ready</span>
              </div>
            </div>

            <div className="md:col-span-5 relative mt-8 md:mt-0">
              <PhoneMockup />
              <FloatingCards />
            </div>
          </div>
        </section>

        {/* VÍDEO EXPLICATIVO — destaque centralizado entre hero e como funciona */}
        <section className="max-w-7xl mx-auto px-6 py-12 md:py-20">
          <div className="text-center mb-8">
            <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-accent-turquesa">
              ▶ Em 1 minuto
            </span>
            <h2 className="font-display text-3xl md:text-4xl mt-3">
              Veja o Anamis em ação
            </h2>
          </div>
          <div className="max-w-5xl mx-auto">
            <VideoExplicativo audiencia="paciente" cor="#a8e6f0" />
          </div>
        </section>

        {/* COMO FUNCIONA */}
        <section className="max-w-7xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <div className="text-center mb-14">
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-turquesa">
              Como funciona
            </span>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mt-3 text-balance max-w-3xl mx-auto">
              Do agendamento ao diagnóstico
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-3 md:gap-4">
            {PASSOS.map((p, i) => (
              <div key={p.titulo} className="relative">
                <div className="rounded-3xl bg-white/8 backdrop-blur-sm border border-white/15 p-6 h-full hover:bg-white/12 hover:border-white/25 transition group">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display text-2xl text-accent-turquesa bg-accent-turquesa/15 border border-accent-turquesa/30">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <span className="text-2xl">{p.icone}</span>
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-white">{p.titulo}</h3>
                  <p className="text-sm text-white/75 leading-relaxed">{p.descricao}</p>
                </div>
                {i < PASSOS.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-px bg-gradient-to-r from-white/30 to-transparent" />
                )}
              </div>
            ))}
          </div>
        </section>

        {/* SEÇÃO PROFISSIONAIS — chama atenção pra outras landings */}
        <section className="max-w-7xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <div className="text-center mb-12">
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-turquesa">
              Profissionais que usam Anamis
            </span>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mt-3">
              Quem cuida de você no Anamis
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            <Link
              to="/medicos"
              className="group rounded-3xl bg-white/8 backdrop-blur-sm border border-white/15 p-7 hover:bg-white/12 hover:border-white/30 transition"
            >
              <div className="text-4xl mb-3">🩺</div>
              <h3 className="font-display text-2xl mb-2">Médicos</h3>
              <p className="text-sm text-white/75 leading-relaxed mb-4">
                Cardiologia, ortopedia, clínica geral, pediatria, ginecologia,
                endocrinologia e mais. Profissionais usam Anamis pra ler sua história
                organizada antes da consulta.
              </p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-turquesa group-hover:translate-x-1 transition">
                É médico? Veja como funciona →
              </span>
            </Link>

            <Link
              to="/psicologos"
              className="group rounded-3xl bg-white/8 backdrop-blur-sm border border-white/15 p-7 hover:bg-white/12 hover:border-white/30 transition"
            >
              <div className="text-4xl mb-3">🧠</div>
              <h3 className="font-display text-2xl mb-2">Psicólogos & psiquiatras</h3>
              <p className="text-sm text-white/75 leading-relaxed mb-4">
                Saúde mental tem fluxo próprio: humor, ansiedade, gatilhos, técnicas de
                coping. Você registra entre as sessões, profissional acompanha continuamente.
              </p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-turquesa group-hover:translate-x-1 transition">
                É psicólogo? Veja como funciona →
              </span>
            </Link>
          </div>
        </section>

        {/* MANIFESTO — pitch core adaptado pra paciente */}
        <SecaoManifesto audiencia="paciente" />

        {/* STATS / NÚMEROS */}
        <section className="max-w-7xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <Stat valor="2 min" rotulo="Por dia, no celular" />
            <Stat valor="100%" rotulo="Grátis pra paciente" />
            <Stat valor="LGPD" rotulo="Compliance total" />
            <Stat valor="∞" rotulo="Médicos vinculados" />
          </div>
        </section>

        {/* FOOTER */}
        <footer className="border-t border-white/10 mt-12">
          <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
            <Logo small />
            <div className="text-xs text-white/55 tracking-wider text-center md:text-right">
              © 2026 Anamis · São Paulo · Confidencial · contato@anamis.ai
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}

const PASSOS = [
  {
    icone: '📅',
    titulo: 'Consulta agendada',
    descricao:
      'Médico marca consulta no sistema e o ciclo de check-ins do paciente é disparado automaticamente.',
  },
  {
    icone: '✍️',
    titulo: 'Check-in diário',
    descricao:
      '2 min por dia. Sintomas, mapa corporal, sono e medicamentos. Adaptativo.',
  },
  {
    icone: '🧠',
    titulo: 'IA processa',
    descricao:
      'Claude correlaciona padrões temporais e gera hipóteses ranqueadas com alertas urgentes.',
  },
  {
    icone: '🩺',
    titulo: 'Médico recebe',
    descricao:
      'Anamnese pronta antes da consulta. Exporta HL7/FHIR pra qualquer prontuário.',
  },
]

function Logo({ small = false }: { small?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`${
          small ? 'w-8 h-8' : 'w-11 h-11'
        } rounded-xl bg-white/15 border border-white/25 flex items-center justify-center backdrop-blur-sm`}
      >
        <svg width={small ? '16' : '20'} height={small ? '16' : '20'} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
          <path d="M3 12h4l3-9 4 18 3-9h4" />
        </svg>
      </div>
      <div>
        <div className={`${small ? 'text-lg' : 'text-2xl'} font-extrabold tracking-tight`}>
          anamis<span className="text-accent-turquesa">.ai</span>
        </div>
      </div>
    </div>
  )
}

function Tick() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function PhoneMockup() {
  return (
    <div className="relative mx-auto" style={{ maxWidth: 320 }}>
      <div className="absolute inset-0 -m-8 rounded-[3rem] bg-accent-turquesa/15 blur-3xl animate-pulse-glow" />
      <div
        className="relative rounded-[2.5rem] bg-deep-950 p-3 shadow-2xl animate-float"
        style={{
          boxShadow:
            '0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08), inset 0 0 0 1px rgba(255,255,255,0.04)',
        }}
      >
        <div className="rounded-[2rem] overflow-hidden bg-gradient-to-br from-anamis-50 via-white to-anamis-100 aspect-[9/19] flex flex-col">
          {/* Notch */}
          <div className="pt-3 pb-2 flex justify-center">
            <div className="w-20 h-1 rounded-full bg-slate-300" />
          </div>

          {/* Header com saudação */}
          <div className="px-5 pt-3 pb-4">
            <div className="text-[10px] uppercase tracking-widest text-anamis-700 font-bold">
              ANAMIS
            </div>
            <div className="font-display text-2xl text-slate-900 mt-1">Olá, Maria</div>
            <div className="text-xs text-slate-500 mt-0.5">Como você está hoje?</div>
          </div>

          {/* Grid de humor */}
          <div className="px-5 mb-4 grid grid-cols-5 gap-1.5">
            {['😢', '😕', '😐', '🙂', '😄'].map((e, i) => (
              <div
                key={i}
                className={`aspect-square rounded-xl flex items-center justify-center text-xl border ${
                  i === 3
                    ? 'bg-anamis-600 border-anamis-700 scale-110 shadow-lg shadow-anamis-600/30'
                    : 'bg-white border-slate-200'
                }`}
              >
                {e}
              </div>
            ))}
          </div>

          {/* Card de check-in */}
          <div className="px-5 mb-3">
            <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
                Streak
              </div>
              <div className="flex items-baseline gap-2">
                <div className="font-display text-3xl text-anamis-700">7</div>
                <div className="text-xs text-slate-600">dias seguidos</div>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-anamis-100 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-anamis-500 to-anamis-700"
                  style={{ width: '70%' }}
                />
              </div>
            </div>
          </div>

          {/* Cards de sintomas */}
          <div className="px-5 mb-3 space-y-2 flex-1">
            <div className="rounded-xl bg-white border border-slate-200 px-3 py-2 flex items-center gap-2 shadow-sm">
              <div className="w-1.5 h-6 rounded bg-yellow-400" />
              <div className="flex-1">
                <div className="text-[10px] text-slate-500 font-medium">Sintoma persistente</div>
                <div className="text-xs font-semibold text-slate-900">Dor de cabeça (3 dias)</div>
              </div>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 px-3 py-2 flex items-center gap-2 shadow-sm">
              <div className="w-1.5 h-6 rounded bg-green-500" />
              <div className="flex-1">
                <div className="text-[10px] text-slate-500 font-medium">Melhorou</div>
                <div className="text-xs font-semibold text-slate-900">Sono · 8h ontem</div>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="px-5 pb-5">
            <div className="rounded-full bg-anamis-600 text-white text-center text-xs font-bold py-2.5 shadow-lg shadow-anamis-600/40">
              Iniciar check-in
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function FloatingCards() {
  return (
    <>
      <div className="hidden lg:block absolute -top-4 -left-8 animate-float" style={{ animationDelay: '0.5s' }}>
        <div className="rounded-2xl bg-deep-800/95 backdrop-blur-md border border-white/30 px-4 py-3 shadow-2xl">
          <div className="text-[9px] uppercase tracking-widest text-accent-turquesa font-bold">
            ✨ IA
          </div>
          <div className="text-xs font-semibold text-white mt-1">Hipótese ranqueada</div>
          <div className="text-[10px] text-white/80 mt-0.5">Cefaleia tensional · 87%</div>
        </div>
      </div>

      <div className="hidden lg:block absolute bottom-12 -right-4 animate-float" style={{ animationDelay: '1s' }}>
        <div className="rounded-2xl bg-deep-800/95 backdrop-blur-md border border-white/30 px-4 py-3 shadow-2xl">
          <div className="text-[9px] uppercase tracking-widest text-accent-turquesa font-bold flex items-center gap-1">
            ⚠ Alerta
          </div>
          <div className="text-xs font-semibold text-white mt-1">Urgente</div>
          <div className="text-[10px] text-white/80 mt-0.5">Dor torácica + dispneia</div>
        </div>
      </div>
    </>
  )
}


function Stat({ valor, rotulo }: { valor: string; rotulo: string }) {
  return (
    <div className="rounded-3xl bg-white/8 border border-white/15 backdrop-blur-sm p-6 hover:bg-white/12 hover:border-white/25 transition">
      <div className="font-display text-4xl md:text-5xl lg:text-6xl text-gradient-cool mb-2">
        {valor}
      </div>
      <div className="text-xs text-white/75 leading-tight uppercase tracking-wider">
        {rotulo}
      </div>
    </div>
  )
}

function Decorations() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* Anéis ECG */}
      <div className="absolute -top-1/3 -right-1/4 w-[120vmin] h-[120vmin] rounded-full border border-white/[0.05]" />
      <div className="absolute -top-1/4 -right-1/6 w-[90vmin] h-[90vmin] rounded-full border border-white/[0.06]" />
      <div className="absolute -top-[10%] -right-[5%] w-[60vmin] h-[60vmin] rounded-full border border-accent-turquesa/[0.1]" />
      <div className="absolute -bottom-1/3 -left-1/4 w-[100vmin] h-[100vmin] rounded-full border border-white/[0.05]" />

      {/* Pontos brilhantes */}
      <div className="absolute top-[15%] right-[20%] w-1 h-1 rounded-full bg-accent-turquesa shadow-[0_0_20px_8px_rgba(168,230,240,0.3)] animate-pulse" />
      <div className="absolute top-[60%] left-[10%] w-1 h-1 rounded-full bg-white/60 shadow-[0_0_15px_5px_rgba(255,255,255,0.2)] animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute bottom-[20%] right-[15%] w-1.5 h-1.5 rounded-full bg-accent-turquesa shadow-[0_0_20px_10px_rgba(168,230,240,0.25)] animate-pulse" style={{ animationDelay: '2s' }} />
    </div>
  )
}
